import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  deleteLegalSkill,
  getLegalSkill,
  listLegalSkills,
  saveLegalSkill,
  type LegalSkillSummary,
} from '@/lib/api-client';

// P2 Section 2.6: load and manage the legal skills that make Court Copilot
// reason like the assigned senior practitioner. Dark stone + amber, matching
// the matter editor and import pages.

const JURISDICTIONS = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT', 'FED', 'CTH'];
const AREAS = [
  'litigation',
  'family',
  'regulatory',
  'finance',
  'tax',
  'property',
  'commercial',
  'consumer',
  'criminal',
  'administrative',
];
const MAX_BODY = 200_000;

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

interface FormState {
  slug: string;
  name: string;
  description: string;
  body: string;
  jurisdictions: string[];
  areasOfLaw: string[];
}

const EMPTY: FormState = {
  slug: '',
  name: '',
  description: '',
  body: '',
  jurisdictions: [],
  areasOfLaw: [],
};

export function AdminLegalSkills() {
  const [skills, setSkills] = useState<LegalSkillSummary[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setSkills(await listLegalSkills().catch(() => []));
  }
  useEffect(() => {
    void refresh();
  }, []);

  const editing = editingSlug !== null;
  const bodyChars = form.body.length;

  async function startEdit(slug: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      const full = await getLegalSkill(slug);
      setForm({
        slug: full.slug,
        name: full.name,
        description: full.description,
        body: full.body,
        jurisdictions: full.jurisdictions,
        areasOfLaw: full.areasOfLaw,
      });
      setEditingSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load skill.');
    }
  }

  function resetForm(): void {
    setForm(EMPTY);
    setEditingSlug(null);
    setError(null);
  }

  async function handleSave(): Promise<void> {
    setError(null);
    setNotice(null);
    if (!form.slug.trim() || !form.name.trim() || !form.body.trim()) {
      setError('Slug, name, and body are required.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setError('Slug must be kebab-case (a-z, 0-9, hyphen).');
      return;
    }
    if (form.body.length > MAX_BODY) {
      setError(`Body too long (${form.body.length} / ${MAX_BODY}).`);
      return;
    }
    setBusy(true);
    try {
      await saveLegalSkill(form);
      setNotice(editing ? 'Skill updated.' : 'Skill added.');
      resetForm();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(slug: string): Promise<void> {
    setError(null);
    try {
      await deleteLegalSkill(slug);
      if (editingSlug === slug) {
        resetForm();
      }
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Legal skills</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>
        <p className="text-sm text-stone-400">
          These skills make Court Copilot reason like the assigned senior practitioner. Load your
          existing user skills here, then assign them to matters by priority.
        </p>

        <Card variant="default" padding="md" className="space-y-4 border-amber-900/30 bg-amber-950/10">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
              {editing ? `Editing ${form.slug}` : 'Add new skill'}
            </p>
            {editing ? (
              <button
                type="button"
                onClick={resetForm}
                className="text-[10px] uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="sk-slug">
                Slug
              </label>
              <input
                id="sk-slug"
                type="text"
                value={form.slug}
                disabled={editing}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="victoria-litigation-lawyer"
                className={`${inputClass} disabled:opacity-60`}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="sk-name">
                Name
              </label>
              <input
                id="sk-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Victoria Litigation Lawyer"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="sk-desc">
              Description
            </label>
            <input
              id="sk-desc"
              type="text"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Short summary of what this skill covers"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ChipMultiSelect
              label="Jurisdictions"
              options={JURISDICTIONS}
              selected={form.jurisdictions}
              onToggle={(v) =>
                setForm((f) => ({ ...f, jurisdictions: toggle(f.jurisdictions, v) }))
              }
            />
            <ChipMultiSelect
              label="Areas of law"
              options={AREAS}
              selected={form.areasOfLaw}
              onToggle={(v) => setForm((f) => ({ ...f, areasOfLaw: toggle(f.areasOfLaw, v) }))}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className={labelClass} htmlFor="sk-body">
                Body (markdown)
              </label>
              <span
                className={bodyChars > MAX_BODY ? 'text-[10px] text-red-400' : 'text-[10px] text-stone-600'}
              >
                {bodyChars.toLocaleString()} / {MAX_BODY.toLocaleString()}
              </span>
            </div>
            <textarea
              id="sk-body"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Paste the full skill markdown here"
              rows={14}
              className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
            />
          </div>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
              {editing ? 'Update skill' : 'Add skill'}
            </button>
          </div>
        </Card>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Loaded skills ({skills.length})
          </p>
          {skills.length === 0 ? (
            <p className="text-sm text-stone-500">No skills loaded yet.</p>
          ) : (
            <div className="space-y-2">
              {skills.map((s) => (
                <div
                  key={s.slug}
                  className="flex items-center justify-between gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2"
                >
                  <button
                    type="button"
                    onClick={() => void startEdit(s.slug)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate text-sm text-stone-200">{s.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                      {s.slug} · v{s.version} · {s.charCount.toLocaleString()} chars ·{' '}
                      {[...s.jurisdictions, ...s.areasOfLaw].join(', ') || 'no tags'}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(s.slug)}
                    aria-label="Deactivate skill"
                    className="shrink-0 rounded p-1 text-stone-500 transition-colors hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ChipMultiSelect({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div>
      <span className={labelClass}>{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={
                on
                  ? 'rounded border border-amber-700/60 bg-amber-950/30 px-2 py-0.5 text-[11px] text-amber-300'
                  : 'rounded border border-stone-800 px-2 py-0.5 text-[11px] text-stone-500 hover:border-stone-700'
              }
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}
